import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Container,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  ChipProps,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import {
  useParams,
  useLocation,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { GET_BOOKINGS_BY_EMAIL } from "../../../../platform/graphql/queries/booking";

/* Logged-in account (provider) */
import { UserContext } from "../../../../platform/components/auth/auth-context";

/* Helpers */
import {
  getResidentLabel,
  getCareNeedRaw,
  mapCareNeed,
  getInstallmentSummaryFromExtra,
} from "../../../../platform/careComponents/helper/referralHelpers";

/* GQL */
import {
  BOOKING_TYPES_READY_FOR_REFERRER,
  GET_MY_REFERRER_PROFILE,
  GET_REFERRER_BY_TOKEN,
} from "../../../../platform/graphql/queries/care";
import {
  CHANGE_REFERRAL_LEAD_STATUS,
  PICK_BROADCAST_WINNER,
  SET_REFERRAL_BROADCAST_GROUP_CLOSED,
  UPSERT_REFERRER_PROFILE,
} from "../../../../platform/graphql/mutations/care";

/* UI blocks */
import ReferrerChatDialog from "../../../../platform/careComponents/components/referralChatDialog";
import TourBookingButton, {
  BusinessBookingConfig,
} from "../../../../platform/careComponents/components/tourBookingButton";
import { rootClientPath } from "../../../../platform/util/link";
import { alpha } from "@mui/material/styles";
import {
  isClosedStatus,
  isOpenStatus,
  statusToChipColor,
} from "../../../../platform/careComponents/utils/statuses";
import {
  buildLeadFromBroadcastInvite,
  findScheduledTourBookingForLead,
  formatTourScheduleLabel,
  getBookedTourMessage,
  getConciergeMetaFromLead,
  getConciergePortalStatusMessage,
  getLeadBroadcastGroupId,
  getLeadProviderContactInfo,
  getTourScheduleFromLead,
  hasReferrerCommunicationStarted,
  parseBroadcastSeed,
  parseExtraLoose,
  parseResidentKeyFromSeed,
  prettyDateLabel,
} from "./referrerPortalShared";

const MY_REFERRAL_BROADCAST_INVITES_COUNT = gql`
  query MyReferralBroadcastInvitesCount {
    myReferralBroadcastInvitesCount
  }
`;

const MY_REFERRAL_BROADCAST_INVITES_FOR_PORTAL = gql`
  query MyReferralBroadcastInvitesForPortal($statuses: [InviteStatus!]) {
    myReferralBroadcastInvites(statuses: $statuses) {
      id
      status
      respondedAt
      providerNote
      business {
        id
        name
        url
        emails
        phoneNumbers {
          countryCode
          phoneNumber
        }
      }
      group {
        id
        seedJson
        createdAt
        closedAt
        winnerInviteId
        ownerAgentId
        ownerAgentName
        ownerAgent {
          id
          username
          email
          phone
        }
      }
    }
  }
`;
/* =============================================================================
   Small utils
   ========================================================================== */

/** Read a query param as a trimmed string, null for "", "undefined", "null" */
function useQueryParam(name: string) {
  const { search } = useLocation();
  return React.useMemo(() => {
    const raw = new URLSearchParams(search).get(name);
    if (raw == null) return null;
    const v = raw.trim();
    const bad =
      v === "" || v.toLowerCase() === "undefined" || v.toLowerCase() === "null";
    return bad ? null : v;
  }, [name, search]);
}

function useRemoveTokenFromUrl() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  return React.useCallback(
    (hasPathParamToken: boolean) => {
      const qs = new URLSearchParams(search);
      qs.delete("token");
      if (hasPathParamToken) {
        navigate(
          {
            pathname: "/referrals/portal",
            search: qs.toString() ? `?${qs.toString()}` : "",
          },
          { replace: true }
        );
      } else {
        navigate(
          { pathname, search: qs.toString() ? `?${qs.toString()}` : "" },
          { replace: true }
        );
      }
    },
    [navigate, pathname, search]
  );
}

const isOpen = (s: string) => isOpenStatus(s);
const isClosed = (s: string) => isClosedStatus(s);

function ProviderContactSummary({ lead }: { lead: any }) {
  const providerName = String(lead?.business?.name || "Provider").trim() || "Provider";
  const { email, phone } = getLeadProviderContactInfo(lead);
  const address =
    String(
      lead?.business?.address ||
      lead?.business?.street ||
      "",
    ).trim() || null;
  const mapHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Provider Contact
      </Typography>
      <Stack spacing={0.75}>
        <Typography variant="body2" color="text.secondary">
          {providerName}
        </Typography>
        {address ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, wordBreak: "break-word" }}>
              Address: {address}
            </Typography>
            {mapHref ? (
              <Tooltip title="Open in Google Maps">
                <IconButton
                  size="small"
                  component="a"
                  href={mapHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LocationOnOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
        {email ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
              Email: {email}
            </Typography>
            <Tooltip title="Send email">
              <IconButton
                size="small"
                component="a"
                href={`mailto:${email}`}
              >
                <EmailOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
        {phone ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
              Phone: {phone}
            </Typography>
            <Tooltip title="Call provider">
              <IconButton
                size="small"
                component="a"
                href={`tel:${phone}`}
              >
                <PhoneOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
        {!email && !phone ? (
          <Typography variant="body2" color="text.secondary">
            Contact details are not available for this provider yet.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ConciergeAgentContactCard({
  agentName,
  agentEmail,
  agentPhone,
}: {
  agentName?: string | null;
  agentEmail?: string | null;
  agentPhone?: string | null;
}) {
  const hasAnyContact = !!(agentName || agentEmail || agentPhone);

  if (!hasAnyContact) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle2" fontWeight={700}>
          Concierge Agent
        </Typography>
        {agentName ? (
          <Typography variant="body2" color="text.secondary">
            {agentName}
          </Typography>
        ) : null}
        {agentEmail ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
              Email: {agentEmail}
            </Typography>
            <Tooltip title="Send email">
              <IconButton size="small" component="a" href={`mailto:${agentEmail}`}>
                <EmailOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
        {agentPhone ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
              Phone: {agentPhone}
            </Typography>
            <Tooltip title="Call agent">
              <IconButton size="small" component="a" href={`tel:${agentPhone}`}>
                <PhoneOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ManageTourButton({
  label = "Change or Cancel Tour",
  email,
  referralLeadId,
  bookingId,
  bookingTime,
}: {
  label?: string;
  email?: string | null;
  referralLeadId?: string | number | null;
  bookingId?: string | number | null;
  bookingTime?: string | null;
}) {
  const params = new URLSearchParams();
  const normalizedEmail = String(email || "").trim();
  if (normalizedEmail) params.set("email", normalizedEmail);
  const normalizedLeadId = String(referralLeadId || "").trim();
  if (normalizedLeadId) params.set("referralLeadId", normalizedLeadId);
  const normalizedBookingId = String(bookingId || "").trim();
  if (normalizedBookingId) params.set("bookingId", normalizedBookingId);
  const normalizedBookingTime = String(bookingTime || "").trim();
  if (normalizedBookingTime) {
    const dt = new Date(normalizedBookingTime);
    if (!Number.isNaN(dt.getTime())) {
      const year = dt.getFullYear();
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      params.set("month", `${year}-${month}-01`);
    }
  }
  return (
    <Button
      size="small"
      variant="contained"
      component={RouterLink}
      to={{
        pathname: "/myschedule",
        search: params.toString() ? `?${params.toString()}` : "",
      }}
    >
      {label}
    </Button>
  );
}

/* =============================================================================
   Booking button visible only when provider enabled it for this referrer
   ========================================================================== */
function ReadyTourButtonForReferrer({
  lead,
  refereeId,
  referrerNameOverride,
  referrerEmailOverride,
  referrerPhoneOverride,
  bizSlug,
  rootClientPath: rootPath,
  label = "Choose Tour Time",
}: {
  lead: any;
  refereeId: number | string | null | undefined;
  referrerNameOverride?: string | null;
  referrerEmailOverride?: string | null;
  referrerPhoneOverride?: string | null;
  bizSlug?: string | null;
  rootClientPath?: string;
  label?: string;
}) {
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const readyLookupPollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  const businessId = lead?.businessId || lead?.business?.id;
  const effectiveReferrerId =
    lead?.referrer?.id ??
    lead?.referrerId ??
    refereeId ??
    null;
  const effectiveBizSlug = bizSlug ?? (lead?.business?.url || null);
  const providerStatus = String(lead?.providerContact?.status || "").toUpperCase();

  const providerName: string =
    lead?.business?.name || lead?.businessName || "Provider";

  const rawRefName =
    referrerNameOverride ??
    lead?.referrer?.contactName ??
    lead?.referrerContact?.name ??
    null;
  const refName = ["referrer", "unknown"].includes(
    String(rawRefName || "").trim().toLowerCase()
  )
    ? null
    : rawRefName;
  const refEmail =
    referrerEmailOverride ??
    lead?.referrer?.email ??
    lead?.referrerContact?.email ??
    null;
  const refPhone =
    referrerPhoneOverride ??
    lead?.referrer?.phone ??
    lead?.referrerContact?.phone ??
    null;

  const providerEmail =
    lead?.business?.email ?? lead?.providerContact?.email ?? null;
  const providerPhone =
    lead?.business?.phone ?? lead?.providerContact?.phone ?? null;
  const conciergeMeta = getConciergeMetaFromLead(lead);
  const { data, loading, error, refetch } = useQuery(BOOKING_TYPES_READY_FOR_REFERRER, {
    variables: {
      businessId: Number(businessId),
      referrerId: Number(effectiveReferrerId),
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    pollInterval: businessId && effectiveReferrerId ? readyLookupPollMs : 0,
    skip: !businessId || !effectiveReferrerId,
  });
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  React.useEffect(() => {
    if (!businessId || !effectiveReferrerId) return;
    const handleReadyChanged = () => {
      refetch?.();
    };
    const handleStorage = (event: StorageEvent) => {
      if (!event.key?.startsWith("referrer-ready:")) return;
      refetch?.();
    };
    window.addEventListener("plany-referrer-ready-changed", handleReadyChanged as EventListener);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("plany-referrer-ready-changed", handleReadyChanged as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, [businessId, effectiveReferrerId, refetch]);

  if (!businessId || !effectiveReferrerId) return null;
  if (providerStatus !== "UNLOCKED") return null;
  if (loading) return null;

  const readyBookingTypes = data?.bookingTypesReadyForReferrer ?? [];
  if (error && !effectiveBizSlug) return null;

  const readyAgentBookings: BusinessBookingConfig[] =
    readyBookingTypes?.map((r: any) => ({
      id: r?.id,
      type: r?.type || "plany365",
      url: r.link,
      allowEmbed: r?.allowEmbed !== false,
      createdByAgentId: r?.createdByAgentId,
    })) ?? [];
  const agentBookings: BusinessBookingConfig[] = readyAgentBookings.length
    ? readyAgentBookings
    : [];
  if (!agentBookings.length) return null;

  return (
    <TourBookingButton
      lead={{
        id: String(lead.id),
        business: { name: providerName },
        coordinatorEmail: conciergeMeta?.agent?.email ?? null,
        coordinatorName: conciergeMeta?.agent?.name ?? null,
        referrerContact: {
          id: String(effectiveReferrerId),
          name: refName,
          email: refEmail,
          phone: refPhone,
        },
        providerContact: {
          name: providerName,
          email: providerEmail,
          phone: providerPhone,
        },
      }}
      actor="REFERRER"
      type="plany365"
      businessBookings={agentBookings}
      bizSlug={effectiveBizSlug}
      rootClientPath={rootPath ?? "/care"}
      label={label}
      onLaunch={() => { }}
    />
  );
}

/* =============================================================================
   Lead Card
   ========================================================================== */
function LeadCard({
  lead,
  refereeId,
  referrerName,
  referrerEmail,
  referrerPhone,
  bizSlug,
  onOpenChat,
  extraActions,
  placementCompleted = false,
  collapsible = false,
  defaultCollapsed = false,
  suppressConciergeBadge = false,
}: {
  lead: any;
  refereeId?: number | string | null;
  referrerName?: string | null;
  referrerEmail?: string | null;
  referrerPhone?: string | null;
  bizSlug?: string | null;
  onOpenChat?: (v: { id: string; referrerName?: string | null }) => void;
  extraActions?: React.ReactNode;
  placementCompleted?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  suppressConciergeBadge?: boolean;
}) {
  const [expanded, setExpanded] = React.useState(!defaultCollapsed);

  const providerName: string = lead?.business?.name;
  const providerTo: string | null = lead?.business?.url
    ? `/${lead.business.url}`
    : null;

  const createdLabel = prettyDateLabel(lead?.createdAt);
  const residentLabel = getResidentLabel(lead);
  const careLabel = mapCareNeed(getCareNeedRaw(lead));
  const inst = getInstallmentSummaryFromExtra(lead?.extra);
  const conciergeMeta = getConciergeMetaFromLead(lead);
  const chatLeadId = String(lead?.id || "").trim();

  // Provider acceptance / unlock signal already used in your file
  const providerStatus: string = (lead?.providerContact?.status ||
    "PENDING_PROVIDER") as string;
  const providerNote: string =
    String(lead?.providerNote ?? lead?.extra?.providerNote ?? "").trim();
  const referrerStatusMessage = getConciergePortalStatusMessage(lead);
  const statusDetail = React.useMemo(() => {
    const normalized = String(lead?.status || providerStatus || "").toUpperCase();
    if (conciergeMeta.isConcierge) {
      return referrerStatusMessage;
    }
    if (providerNote) return providerNote;
    if (referrerStatusMessage) return referrerStatusMessage;
    if (normalized === "DECLINED") return "Provider declined this referral.";
    if (normalized === "NO_RESPONSE") return "Provider has not responded yet.";
    if (normalized === "EXPIRED") return "This invite expired without a response.";
    if (normalized === "ACCEPTED") return "Accepted by care provider. Provider contact details are now available below.";
    return "";
  }, [
    conciergeMeta.isConcierge,
    lead?.status,
    providerNote,
    providerStatus,
    referrerStatusMessage,
  ]);
  const statusDetailColor = React.useMemo(() => {
    const normalized = String(lead?.status || providerStatus || "").toUpperCase();
    if (conciergeMeta.isConcierge && normalized === "ACCEPTED") return "info.main";
    if (referrerStatusMessage) return "info.main";
    if (normalized === "DECLINED") return "error.main";
    if (normalized === "NO_RESPONSE") return "info.main";
    if (normalized === "EXPIRED") return "text.secondary";
    if (normalized === "ACCEPTED") return "success.main";
    return "text.secondary";
  }, [conciergeMeta.isConcierge, lead?.status, providerStatus, referrerStatusMessage]);


  // If your backend marks a chosen provider for a group, detect it to avoid duplicate “Choose”
  const businessId = lead?.businessId || lead?.business?.id || null;
  const bookedTour = getTourScheduleFromLead(lead);
  const isTourScheduled = !!bookedTour || String(lead?.status || "").toUpperCase() === "TOUR_SCHEDULED";
  const visibleStatusDetail = isTourScheduled ? "" : statusDetail;
  const selectedBusinessId: string | number | null =
    lead?.group?.selectedBusinessId ?? lead?.selectedBusinessId ?? null;
  const isAlreadySelected =
    selectedBusinessId != null &&
    businessId != null &&
    String(selectedBusinessId) === String(businessId);
  const displayLeadStatus =
    String(lead?.status || "").toUpperCase() === "PLACED"
      ? "COMPLETED"
      : isAlreadySelected && !isTourScheduled
        ? "SELECTED"
        : lead.status;
  const displayStatusDetail =
    String(lead?.status || "").toUpperCase() === "PLACED"
      ? "Placement completed."
      : isAlreadySelected && !isTourScheduled
        ? "You selected this provider for the next step."
        : visibleStatusDetail;
  const displayStatusColor: ChipProps["color"] =
    String(lead?.status || "").toUpperCase() === "PLACED"
      ? "success"
      : isAlreadySelected && !isTourScheduled
        ? "success"
        : statusToChipColor(lead.status) ?? "default";
  const providerAccepted =
    String(lead?.providerContact?.status || "").toUpperCase() === "UNLOCKED" ||
    String(lead?.status || "").toUpperCase() === "ACCEPTED";
  const leadExtra = parseExtraLoose(lead?.extra) || {};
  const normalizedLeadPayerType = String(
    lead?.payerType ||
      leadExtra?.payerType ||
      leadExtra?.pricing?.payerType ||
      ""
  )
    .trim()
    .toUpperCase();
  const showConciergeChip =
    !suppressConciergeBadge &&
    conciergeMeta.isConcierge &&
    normalizedLeadPayerType === "PRIVATE";

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 1 }}>
          {residentLabel}
        </Typography>

        <Chip
          label={displayLeadStatus}
          size="small"
          color={displayStatusColor}
        />

        {providerAccepted && !isTourScheduled && (
          <Chip
            size="small"
            color="success"
            variant="filled"
            label="Provider Accepted"
          />
        )}

        {showConciergeChip && (
          <Chip
            size="small"
            color="secondary"
            variant="outlined"
            label={conciergeMeta.isRush ? "Concierge • Rush" : "Concierge"}
          />
        )}

        {inst.status === "PARTIAL" && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Partial • ${inst.paidCount}/${inst.count}`}
          />
        )}
        {inst.status === "PAID" && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Paid in Full"
          />
        )}

        {providerName && (
          <Chip
            size="small"
            label={providerName}
            component={providerTo ? RouterLink : "div"}
            to={providerTo || undefined}
            clickable={!!providerTo}
            variant="outlined"
          />
        )}

        {/* If this provider is already selected for the group, show a clear badge */}
        {isAlreadySelected && (
          <Chip size="small" color="success" label="Selected" />
        )}
        {placementCompleted && !isTourScheduled && (
          <Chip size="small" color="success" variant="filled" label="Completed" />
        )}
        {isTourScheduled && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={
              bookedTour?.bookingTime
                ? `Tour Scheduled: ${formatTourScheduleLabel(bookedTour.bookingTime) ?? new Date(bookedTour.bookingTime).toLocaleString()}`
                : "Tour Scheduled"
            }
          />
        )}

        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          {/* Let referrer book a tour when provider unlocked it */}
          {expanded && !isTourScheduled && (
            <ReadyTourButtonForReferrer
              lead={lead}
              refereeId={refereeId ?? lead?.referrer?.id ?? null}
              referrerNameOverride={referrerName}
              referrerEmailOverride={referrerEmail}
              referrerPhoneOverride={referrerPhone}
              bizSlug={bizSlug}
              rootClientPath={rootClientPath}
              label="Choose Tour Time"
            />
          )}
          {expanded && isTourScheduled ? (
            <ManageTourButton
              email={
                referrerEmail ||
                lead?.referrer?.email ||
                lead?.referrerContact?.email ||
                null
              }
              referralLeadId={lead?.id ?? null}
              bookingId={bookedTour?.id ?? null}
              bookingTime={bookedTour?.bookingTime ?? null}
            />
          ) : null}
          {expanded && conciergeMeta.isConcierge && chatLeadId && onOpenChat ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                onOpenChat({
                  id: chatLeadId,
                  referrerName: referrerName || null,
                })
              }
            >
              Chat with Agent
            </Button>
          ) : null}

          {expanded ? extraActions : null}
          {collapsible ? (
            <Tooltip title={expanded ? "Collapse details" : "Expand details"}>
              <IconButton
                size="small"
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                  border: "1px solid rgba(103, 114, 126, 0.18)",
                  borderRadius: 999,
                }}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
        {careLabel && careLabel !== "—" && (
          <Chip size="small" label={`Care: ${careLabel}`} />
        )}
        {lead.payerType && (
          <Chip size="small" label={`Payer: ${lead.payerType}`} />
        )}
        {lead.urgency && (
          <Chip size="small" label={`Urgency: ${lead.urgency}`} />
        )}
        {createdLabel && (
          <Chip size="small" label={createdLabel} variant="outlined" />
        )}
        </Stack>

        {displayStatusDetail && (
        <Typography
          variant="body2"
          sx={{ mt: 1, color: statusDetailColor, fontWeight: 600 }}
        >
          {displayStatusDetail}
        </Typography>
        )}

        {conciergeMeta.isConcierge ? (
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <ProviderContactSummary lead={lead} />
          <ConciergeAgentContactCard
            agentName={conciergeMeta?.agent?.name ?? null}
            agentEmail={conciergeMeta?.agent?.email ?? null}
            agentPhone={conciergeMeta?.agent?.phone ?? null}
          />
        </Box>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <ProviderContactSummary lead={lead} />
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

/* =============================================================================
   Profile form — lock Name + Email to account; allow Organization + Phone edits
   ========================================================================== */
function ReferrerProfileForm({
  initial,
  onSaved,
  lockedContact,
  titleOverride,
}: {
  initial?: {
    contactName?: string | null;
    orgName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  onSaved?: () => void | Promise<void>;
  /** pass only contactName + email here (we keep phone editable) */
  lockedContact?: {
    contactName?: string | null;
    email?: string | null;
  } | null;
  titleOverride?: string;
}) {
  const isLocked = !!lockedContact;

  // ---------- helpers for consistent comparisons ----------
  const normText = (v?: string | null) => (v ?? "").replace(/\s+/g, " ").trim();

  const normPhone = (v?: string | null) => {
    const digits = (v ?? "").replace(/\D+/g, "");
    return digits; // keep digits-only for stable equality
  };

  // ---------- controlled state ----------
  const [values, setValues] = React.useState({
    contactName: initial?.contactName ?? "",
    orgName: initial?.orgName ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
  });

  // Baseline snapshot we compare against (tracks server state)
  const baselineRef = React.useRef({
    orgName: normText(initial?.orgName),
    phone: normPhone(initial?.phone),
  });

  // sync state + baseline when "initial" changes from query
  React.useEffect(() => {
    setValues((s) => ({
      ...s,
      contactName: isLocked
        ? lockedContact?.contactName ?? ""
        : initial?.contactName ?? "",
      email: isLocked ? lockedContact?.email ?? "" : initial?.email ?? "",
      phone: initial?.phone ?? "",
      orgName: initial?.orgName ?? "",
    }));

    baselineRef.current = {
      orgName: normText(initial?.orgName),
      phone: normPhone(initial?.phone),
    };
  }, [
    isLocked,
    lockedContact?.contactName,
    lockedContact?.email,
    initial?.contactName,
    initial?.email,
    initial?.phone,
    initial?.orgName,
  ]);

  const [upsert, { loading, error }] = useMutation(UPSERT_REFERRER_PROFILE, {
    refetchQueries: ["MyReferrerProfile"],
    awaitRefetchQueries: true,
  });
  const [submitMessage, setSubmitMessage] = React.useState<string | null>(null);

  const onChange =
    (k: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setValues((s) => ({ ...s, [k]: e.target.value }));

  // ---------- dirty check (only orgName + phone matter server-side) ----------
  const isDirty =
    normText(values.orgName) !== baselineRef.current.orgName ||
    normPhone(values.phone) !== baselineRef.current.phone;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitMessage(null);

    // nothing changed -> do nothing
    if (!isDirty) return;

    const input = {
      orgName: normText(values.orgName) || null,
      phone: normPhone(values.phone) || null,
    };

    const result = await upsert({
      variables: { input },
      optimisticResponse: undefined,
    });

    const payload = result?.data?.upsertReferrerProfile;
    if (!payload?.success) {
      setSubmitMessage(payload?.message || "Could not save profile changes.");
      return;
    }

    // update baseline so the button disables again
    baselineRef.current = {
      orgName: input.orgName ?? "",
      phone: input.phone ?? "",
    };

    await onSaved?.();
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {titleOverride ??
          (initial
            ? "Update Your Referrer Profile"
            : "Create Your Referrer Profile")}
      </Typography>

      {isLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your <strong>Name</strong> and <strong>Email</strong> come from your
          account. You can edit your <strong>Organization</strong> and{" "}
          <strong>Phone</strong> here.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      {submitMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitMessage}
        </Alert>
      )}

      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Contact Name"
            value={
              isLocked ? lockedContact?.contactName ?? "" : values.contactName
            }
            onChange={isLocked ? undefined : onChange("contactName")}
            fullWidth
            disabled={isLocked}
            InputProps={isLocked ? { readOnly: true } : undefined}
          />
          <TextField
            label="Organization"
            value={values.orgName}
            onChange={onChange("orgName")}
            fullWidth
          />
          <TextField
            type="email"
            label="Email"
            value={isLocked ? lockedContact?.email ?? "" : values.email}
            onChange={isLocked ? undefined : onChange("email")}
            fullWidth
            disabled={isLocked}
            InputProps={isLocked ? { readOnly: true } : undefined}
          />
          <TextField
            label="Phone"
            value={values.phone}
            onChange={onChange("phone")}
            fullWidth
          />
          <Stack direction="row" gap={1}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={loading || !isDirty}
            >
              {initial ? "Save Changes" : "Save"}
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={!isDirty || loading}
              onClick={() =>
                setValues((s) => ({
                  ...s,
                  orgName: baselineRef.current.orgName,
                  phone: baselineRef.current.phone,
                }))
              }
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}

/* =============================================================================
   TokenEntry
   ========================================================================== */
function TokenEntry() {
  const [manualToken, setManualToken] = React.useState<string>("");
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Track Referrals
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Portal token"
          value={manualToken}
          onChange={(e) => setManualToken(e.target.value)}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={() => setManualToken((t) => t.trim())}
          disabled={!manualToken.trim()}
        >
          Load
        </Button>
      </Stack>
    </Paper>
  );
}

/* =============================================================================
   Main Page with Tabs
   ========================================================================== */
const ReferrerPortalPage: React.FC = () => {
  const { token: tokenFromParams } = useParams<{ token?: string }>();
  const tokenFromQuery = useQueryParam("token");
  const hasPathParamToken = Boolean(tokenFromParams);
  const effectiveToken = tokenFromParams ?? tokenFromQuery ?? "";
  const tokenMode = Boolean(effectiveToken);

  /* Logged-in provider user */
  const { providerUser } = React.useContext(UserContext);
  const isLoggedIn = Boolean(
    (providerUser as any)?.id ||
    (providerUser as any)?.email ||
    (providerUser as any)?.primaryEmail
  );

  // Derive contact from account: prefer username, fall back to email
  const accountContact = React.useMemo(() => {
    if (!isLoggedIn) return null;
    const contactName =
      ((providerUser as any)?.displayName ?? "").trim() ||
      (providerUser?.username ?? "").trim() ||
      ((providerUser as any)?.email ?? (providerUser as any)?.primaryEmail ?? "");
    return {
      contactName: contactName || "",
      email: (providerUser as any)?.email ?? (providerUser as any)?.primaryEmail ?? "",
      // not in user table; we won't lock phone
      phone: (providerUser as any)?.phone ?? (providerUser as any)?.primaryPhone ?? "",
    };
  }, [isLoggedIn, providerUser]);

  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = React.useState(0);
  const handleTab = (_: React.SyntheticEvent, v: number) => setTab(v);

  /** Page-level filter state */
  type Filter = "all" | "open" | "closed";
  const [filter, setFilter] = React.useState<Filter>("all");

  const [dataMode, setDataMode] = React.useState<"token" | "mine">(
    tokenMode ? "token" : "mine"
  );
  const [refChat, setRefChat] = React.useState<null | {
    id: string;
    referrerName?: string | null;
  }>(null);

  React.useEffect(() => setDataMode(tokenMode ? "token" : "mine"), [tokenMode]);

  const refByTokenQuery = useQuery(GET_REFERRER_BY_TOKEN, {
    variables: { token: effectiveToken },
    skip: !tokenMode,
    fetchPolicy: "cache-and-network",
  });

  const myProfileQuery = useQuery(GET_MY_REFERRER_PROFILE, {
    skip: !isLoggedIn,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const [upsertProfile] = useMutation(UPSERT_REFERRER_PROFILE, {
    refetchQueries: ["MyReferrerProfile"],
    awaitRefetchQueries: true,
  });

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const hasBookingReturnMarker =
      String(params.get("tourBooked") || "").trim() === "1";
    let hasSessionBookingReturn = false;
    try {
      hasSessionBookingReturn = !!sessionStorage.getItem("referrerTourBookingSuccess");
    } catch (_err) {
      hasSessionBookingReturn = false;
    }
    if (!hasBookingReturnMarker && !hasSessionBookingReturn) return;
    refByTokenQuery.refetch?.();
    myProfileQuery.refetch?.();
  }, [location.search, myProfileQuery, refByTokenQuery]);

  const autoCreateInFlight = React.useRef(false);
  const [autoCreated, setAutoCreated] = React.useState(false);

  // ✅ AUTO-CREATE profile after login if it doesn't exist
  React.useEffect(() => {
    if (!isLoggedIn) return;
    if (myProfileQuery.loading) return;
    if (myProfileQuery.error) return;
    const myProfile = myProfileQuery.data?.myReferrerProfile ?? null;
    if (myProfile) return;
    if (!accountContact) return;
    if (autoCreateInFlight.current) return;

    autoCreateInFlight.current = true;
    (async () => {
      try {
        await upsertProfile({
          variables: {
            input: {
              phone: null,
              orgName: null,
            },
          },
        });
        setAutoCreated(true);
      } finally {
        autoCreateInFlight.current = false;
      }
    })();
  }, [
    isLoggedIn,
    accountContact,
    myProfileQuery.loading,
    myProfileQuery.error,
    myProfileQuery.data,
    upsertProfile,
  ]);

  const loading = refByTokenQuery.loading || myProfileQuery.loading;
  const showTokenError = tokenMode && refByTokenQuery.error;
  const showMeError = isLoggedIn && myProfileQuery.error;

  const refereeByToken = refByTokenQuery.data?.referrerByPortalToken ?? null;
  const myProfile = myProfileQuery.data?.myReferrerProfile ?? null;

  const leadsSource = dataMode === "token" ? refereeByToken : myProfile;
  const rawLeads = React.useMemo(() => leadsSource?.referralLead ?? [], [leadsSource]);

  // Parent-level filtered list (child receives both rawLeads and filtered leads)
  const leads = React.useMemo(() => {
    if (!rawLeads || rawLeads.length === 0) return [];
    if (filter === "open") return rawLeads.filter((l: any) => isOpen(l.status));
    if (filter === "closed")
      return rawLeads.filter((l: any) => isClosed(l.status));
    return rawLeads;
  }, [rawLeads, filter]);

  const onRefresh = () => {
    refByTokenQuery.refetch?.();
    myProfileQuery.refetch?.();
  };

  const removeTokenFromUrl = useRemoveTokenFromUrl();

  // Active refereeId for booking
  const activeReferrerId =
    (dataMode === "mine" ? myProfile?.id : refereeByToken?.id) ?? null;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {loading && <LinearProgress />}

      <Container sx={{ py: 4, maxWidth: "md" as any }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              Referral Portal
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => navigate("/care/request?mode=broadcast")}
            >
              New Referral (Broadcast)
            </Button>

            <Button variant="outlined" onClick={() => navigate("/care")}>
              Browse Providers
            </Button>
            <Button variant="outlined" onClick={onRefresh}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {isLoggedIn && tokenMode && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant={dataMode === "mine" ? "contained" : "outlined"}
                  onClick={() => setDataMode("mine")}
                >
                  View My Referrals
                </Button>
                <Button
                  size="small"
                  variant={dataMode === "token" ? "contained" : "outlined"}
                  onClick={() => setDataMode("token")}
                >
                  View Token
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => removeTokenFromUrl(!!hasPathParamToken)}
                >
                  Remove Token
                </Button>
              </Stack>
            }
          >
            You’re signed in and viewing a shared referral via token. Switch
            datasets or remove the token from the URL.
          </Alert>
        )}

        {(showTokenError || showMeError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(refByTokenQuery.error?.message ||
              myProfileQuery.error?.message) ??
              "Error loading data."}
          </Alert>
        )}
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: (t) => alpha(t.palette.info.main, 0.08),
            border: (t) => `1px solid ${alpha(t.palette.info.main, 0.3)}`,
          }}
        >
          This section is for <strong>referrers</strong> who send residents to
          care providers.
        </Alert>

        {isLoggedIn && autoCreated && !myProfile && (
          <Alert severity="success" sx={{ mb: 2 }}>
            We set up your profile from your account. Add your{" "}
            <strong>Organization</strong> and <strong>Phone</strong> below to
            complete it.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={handleTab}>
            <Tab label="Referrals" />
            <Tab label="My Profile" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 0 && (
              <ReferralsTab
                isLoggedIn={isLoggedIn}
                tokenMode={tokenMode}
                dataMode={dataMode}
                setDataMode={setDataMode}
                leads={leads}
                rawLeads={rawLeads}
                refByToken={refereeByToken}
                myProfile={myProfile}
                activeReferrerId={activeReferrerId}
                onOpenChat={(payload) => setRefChat(payload)}
                onRefresh={onRefresh}
                /* NEW: filter props */
                filter={filter}
                setFilter={setFilter}
              />
            )}

            {tab === 1 && (
              <>
                {!isLoggedIn ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      gutterBottom
                    >
                      Sign in to view and edit your referrer profile
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      We’ll show data linked to your account after login.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={() =>
                          navigate(
                            `/login?returnTo=${encodeURIComponent(
                              window.location.pathname + window.location.search
                            )}`
                          )
                        }
                      >
                        Log In
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/signup?returnTo=${encodeURIComponent(
                              window.location.pathname + window.location.search
                            )}`
                          )
                        }
                      >
                        Sign Up
                      </Button>
                    </Stack>
                  </Paper>
                ) : null}

                {(myProfile ?? (tokenMode ? refereeByToken : null)) && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, mt: 2 }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {isLoggedIn
                        ? "Referrer Profile"
                        : "Referrer (read-only)"}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {(() => {
                      const base =
                        isLoggedIn && accountContact
                          ? {
                            contactName: accountContact.contactName,
                            email: accountContact.email,
                          }
                          : {
                            contactName: (myProfile ?? refereeByToken)
                              ?.contactName,
                            email: (myProfile ?? refereeByToken)?.email,
                          };
                      const orgSource = (myProfile ?? refereeByToken) || {};
                      const phoneValue =
                        myProfile?.phone ?? accountContact?.phone ?? "";
                      return (
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {base.contactName && (
                            <Chip label={base.contactName} />
                          )}
                          {orgSource?.orgName && (
                            <Chip label={orgSource.orgName} />
                          )}
                          {base.email && <Chip label={base.email} />}
                          {phoneValue && <Chip label={phoneValue} />}
                        </Stack>
                      );
                    })()}
                  </Paper>
                )}

                {isLoggedIn && (
                  <Box sx={{ mt: 2 }}>
                    <ReferrerProfileForm
                      initial={
                        myProfile
                          ? {
                            contactName: myProfile.contactName,
                            orgName: myProfile.orgName,
                            email: myProfile.email,
                            phone: myProfile.phone,
                          }
                          : null
                      }
                      lockedContact={{
                        contactName: accountContact?.contactName ?? null,
                        email: accountContact?.email ?? null,
                      }}
                      titleOverride={
                        myProfile
                          ? undefined
                          : "Complete Your Profile (add your Organization & Phone)"
                      }
                      onSaved={async () => {
                        await myProfileQuery.refetch?.();
                      }}
                    />
                  </Box>
                )}

                {!isLoggedIn && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Sign in to create or edit your profile. The token view is
                    read-only.
                  </Alert>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  To view referral activity directly, switch to the Referrals
                  tab. You can also open a token link:{" "}
                  <code>/care/track/&lt;token&gt;</code>.
                </Alert>
              </>
            )}
          </Box>

          <ReferrerChatDialog
            open={!!refChat}
            onClose={() => setRefChat(null)}
            leadId={refChat?.id || null}
            referrerName={refChat?.referrerName}
          />
        </Paper>
      </Container>
    </Box>
  );
};

/* =============================================================================
   Referrals Tab
   ========================================================================== */
function ReferralsTab({
  isLoggedIn,
  tokenMode,
  dataMode,
  setDataMode,
  leads,
  rawLeads,
  refByToken,
  myProfile,
  activeReferrerId,
  onOpenChat,
  onRefresh,
  /* NEW: filter props */
  filter,
  setFilter,
}: {
  isLoggedIn: boolean;
  tokenMode: boolean;
  dataMode: "token" | "mine";
  setDataMode: (m: "token" | "mine") => void;
  leads: any[];
  rawLeads: any[];
  refByToken: any;
  myProfile: any;
  activeReferrerId: any;
  onOpenChat: (v: { id: string; referrerName?: string | null }) => void;
  onRefresh: () => void;
  filter: "all" | "open" | "closed";
  setFilter: (f: "all" | "open" | "closed") => void;
}) {
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const bookingLookupPollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  type InviteWorkflowTab =
    | "all"
    | "action"
    | "waiting"
    | "selected"
    | "scheduled"
    | "completed"
    | "closed";
  type InvitePayerTab = "private" | "nonPrivate" | "all";
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteTab, setInviteTab] = React.useState<InviteWorkflowTab>("action");
  const [invitePayerTab, setInvitePayerTab] = React.useState<InvitePayerTab>("private");
  const inviteCountQuery = useQuery(MY_REFERRAL_BROADCAST_INVITES_COUNT, {
    skip: !isLoggedIn,
    fetchPolicy: "cache-and-network",
  });
  const inviteListQuery = useQuery(MY_REFERRAL_BROADCAST_INVITES_FOR_PORTAL, {
    variables: { statuses: ["DRAFT", "SENT", "NO_RESPONSE", "ACCEPTED", "DECLINED", "EXPIRED"] },
    skip: !isLoggedIn || dataMode !== "mine",
    fetchPolicy: "cache-and-network",
  });
  const [pickBroadcastWinner, { loading: pickingWinner }] = useMutation(
    PICK_BROADCAST_WINNER,
    {
      onCompleted: async () => {
        await Promise.all([inviteListQuery.refetch?.(), onRefresh()]);
      },
    }
  );
  const [markPlacementComplete, { loading: markingPlacementComplete }] =
    useMutation(CHANGE_REFERRAL_LEAD_STATUS, {
      onCompleted: async () => {
        await onRefresh();
      },
    });
  const [setGroupClosed, { loading: closingGroup }] = useMutation(
    SET_REFERRAL_BROADCAST_GROUP_CLOSED,
    {
      onCompleted: async () => {
        await Promise.all([inviteListQuery.refetch?.(), onRefresh()]);
      },
    }
  );
  const activeReferrerEmail = String(
    (dataMode === "mine" ? myProfile?.email : refByToken?.email) ||
    myProfile?.email ||
    refByToken?.email ||
    ""
  ).trim();
  const referrerBookingsQuery = useQuery(GET_BOOKINGS_BY_EMAIL, {
    variables: {
      email: activeReferrerEmail,
      selectedYear: new Date().getFullYear(),
    },
    skip: !activeReferrerEmail,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    pollInterval: activeReferrerEmail ? bookingLookupPollMs : 0,
  });
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const referrerBookings = React.useMemo(
    () => (referrerBookingsQuery.data?.getBookingsByEmail ?? []) as any[],
    [referrerBookingsQuery.data?.getBookingsByEmail]
  );
  const portalReferrer = React.useMemo(() => {
    const referrer = dataMode === "mine" ? myProfile : refByToken;
    return {
      id: referrer?.id ?? null,
      name: referrer?.contactName || null,
      email: referrer?.email || null,
      phone: referrer?.phone || null,
    };
  }, [dataMode, myProfile, refByToken]);
  const broadcastInviteCount = Number(
    inviteCountQuery.data?.myReferralBroadcastInvitesCount ?? 0
  );
  const hasBroadcastActivity = Number.isFinite(broadcastInviteCount) && broadcastInviteCount > 0;
  const broadcastInvites = React.useMemo(
    () => (inviteListQuery.data?.myReferralBroadcastInvites ?? []) as any[],
    [inviteListQuery.data?.myReferralBroadcastInvites]
  );
  const invitesForMyReferrals = React.useMemo(() => {
    if (!Array.isArray(broadcastInvites)) return [];
    return [...broadcastInvites].sort((a: any, b: any) => {
      const ta = Date.parse(a?.group?.createdAt || a?.respondedAt || "") || 0;
      const tb = Date.parse(b?.group?.createdAt || b?.respondedAt || "") || 0;
      return tb - ta;
    });
  }, [broadcastInvites]);
  const bookingReturnOverride = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const tourBooked = String(params.get("tourBooked") || "").trim() === "1";
    const bookedLeadId = String(params.get("bookedLeadId") || "").trim();
    const bookedTourTime = String(params.get("bookedTourTime") || "").trim();
    const bookedTourBusiness = String(params.get("bookedTourBusiness") || "").trim();
    if (tourBooked && (bookedLeadId || bookedTourBusiness || bookedTourTime)) {
      return {
        bookedLeadId: bookedLeadId || "",
        bookedTourTime: bookedTourTime || null,
        bookedTourBusiness: bookedTourBusiness || null,
      };
    }
    try {
      const raw = sessionStorage.getItem("referrerTourBookingSuccess");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const savedAt = String(parsed?.savedAt || "").trim();
      if (savedAt) {
        const ageMs = Date.now() - new Date(savedAt).getTime();
        if (Number.isFinite(ageMs) && ageMs > 30 * 60 * 1000) {
          sessionStorage.removeItem("referrerTourBookingSuccess");
          return null;
        }
      }
      return {
        bookedLeadId: String(parsed?.bookedLeadId || "").trim(),
        bookedTourTime: String(parsed?.bookedTourTime || "").trim() || null,
        bookedTourBusiness: String(parsed?.bookedTourBusiness || "").trim() || null,
      };
    } catch (_err) {
      return null;
    }
  }, [location.search]);
  const applyBookingReturnOverride = React.useCallback((lead: any) => {
    if (!lead || !bookingReturnOverride) return lead;
    const leadId = String(lead?.id || "").trim();
    const overrideLeadId = String(bookingReturnOverride.bookedLeadId || "").trim();
    const leadBusinessName = String(lead?.business?.name || "").trim().toLowerCase();
    const overrideBusinessName = String(
      bookingReturnOverride.bookedTourBusiness || ""
    )
      .trim()
      .toLowerCase();
    const matchesLeadId = !!overrideLeadId && leadId === overrideLeadId;
    const matchesBusiness =
      !overrideLeadId &&
      !!overrideBusinessName &&
      !!leadBusinessName &&
      leadBusinessName === overrideBusinessName;
    if (!matchesLeadId && !matchesBusiness) {
      return lead;
    }
    const extra = parseExtraLoose(lead?.extra) || {};
    return {
      ...lead,
      status: "TOUR_SCHEDULED",
      extra: {
        ...extra,
        tourSchedule: {
          ...(extra?.tourSchedule || {}),
          bookingTime:
            bookingReturnOverride.bookedTourTime ||
            extra?.tourSchedule?.bookingTime ||
            null,
          businessName:
            bookingReturnOverride.bookedTourBusiness ||
            extra?.tourSchedule?.businessName ||
            lead?.business?.name ||
            null,
        },
      },
    };
  }, [bookingReturnOverride]);
  const applyScheduledBookingFallback = React.useCallback((lead: any) => {
    if (!lead) return lead;
    const scheduledBooking = findScheduledTourBookingForLead(lead, referrerBookings);
    if (scheduledBooking) {
      const extra = parseExtraLoose(lead?.extra) || {};
      return {
        ...lead,
        status: "TOUR_SCHEDULED",
        extra: {
          ...extra,
          tourSchedule: {
            ...(extra?.tourSchedule || {}),
            bookingId:
              scheduledBooking.id ||
              extra?.tourSchedule?.bookingId ||
              null,
            bookingTime: scheduledBooking.bookingTime || null,
            businessName: scheduledBooking.businessName || lead?.business?.name || null,
          },
        },
      };
    }

    return lead;
  }, [referrerBookings]);
  const applyScheduledLeadState = React.useCallback((lead: any) => {
    return applyScheduledBookingFallback(applyBookingReturnOverride(lead));
  }, [applyBookingReturnOverride, applyScheduledBookingFallback]);
  const normalizeLead = React.useCallback((lead: any) => {
    return applyScheduledLeadState(lead);
  }, [applyScheduledLeadState]);
  const leadsByGroupAndBusiness = React.useMemo(() => {
    const map = new Map<string, any>();
    (rawLeads ?? []).forEach((lead: any) => {
      const groupId = getLeadBroadcastGroupId(lead);
      const businessId = lead?.business?.id ?? lead?.businessId ?? null;
      if (!groupId || !businessId) return;
      map.set(`${String(groupId)}::${String(businessId)}`, lead);
    });
    return map;
  }, [rawLeads]);
  const leadsByGroup = React.useMemo(() => {
    const map = new Map<string, any>();
    (rawLeads ?? []).forEach((lead: any) => {
      const groupId = getLeadBroadcastGroupId(lead);
      if (!groupId) return;
      if (!map.has(String(groupId))) {
        map.set(String(groupId), lead);
      }
    });
    return map;
  }, [rawLeads]);
  const isInviteAcceptedForReferrer = React.useCallback((inv: any) => {
    const seed = parseBroadcastSeed(inv?.group?.seedJson);
    const isPrivateInvite = seed?.payerType === "PRIVATE";
    if (isPrivateInvite) {
      const matchedLead = applyScheduledLeadState(
        leadsByGroupAndBusiness.get(
          `${String(inv?.group?.id ?? "")}::${String(inv?.business?.id ?? "")}`
        )
      );
      return String(matchedLead?.providerContact?.status || "").toUpperCase() === "UNLOCKED";
    }
    return String(inv?.status || "").toUpperCase() === "ACCEPTED";
  }, [applyScheduledLeadState, leadsByGroupAndBusiness]);
  const isInviteActionableAccepted = React.useCallback((inv: any) => {
    const winnerInviteId = String(inv?.group?.winnerInviteId || "").trim();
    if (winnerInviteId) return false;
    return isInviteAcceptedForReferrer(inv);
  }, [isInviteAcceptedForReferrer]);
  const groupedInviteEntries = React.useMemo(() => {
    const grouped = new Map<string, any[]>();
    invitesForMyReferrals.forEach((inv: any) => {
      const requestGroupId = String(inv?.group?.id || "").trim();
      const groupingKey = requestGroupId || `groupless-${String(inv?.id || "")}`;
      const items = grouped.get(groupingKey) ?? [];
      items.push(inv);
      grouped.set(groupingKey, items);
    });

    return Array.from(grouped.entries())
      .sort(([, invitesA], [, invitesB]) => {
        const latestA = Math.max(
          ...invitesA.map(
            (inv: any) =>
              Date.parse(inv?.group?.createdAt || inv?.respondedAt || "") || 0
          )
        );
        const latestB = Math.max(
          ...invitesB.map(
            (inv: any) =>
              Date.parse(inv?.group?.createdAt || inv?.respondedAt || "") || 0
          )
        );
        return latestB - latestA;
      })
      .map(([groupKey, invites]) => {
        const firstSeed = parseBroadcastSeed(invites?.[0]?.group?.seedJson);
        const residentId =
          parseResidentKeyFromSeed(invites?.[0]?.group?.seedJson) ||
          `Group-${String(invites?.[0]?.group?.id || groupKey)}`;
        const isPrivateGroup = firstSeed?.payerType === "PRIVATE";
        const winnerInviteId = String(invites?.[0]?.group?.winnerInviteId || "").trim();
        const isGroupClosed = Boolean(invites?.[0]?.group?.closedAt);
        const groupHasWinner = Boolean(winnerInviteId);
        const matchedLeads = invites
          .map((inv: any) =>
            normalizeLead(
              leadsByGroupAndBusiness.get(
                `${String(inv?.group?.id ?? "")}::${String(inv?.business?.id ?? "")}`
              )
            )
          )
          .filter(Boolean);
        const hasCompleted = matchedLeads.some(
          (lead: any) => String(lead?.status || "").toUpperCase() === "PLACED"
        );
        const hasScheduled = matchedLeads.some((lead: any) => {
          return (
            !!getTourScheduleFromLead(lead) ||
            String(lead?.status || "").toUpperCase() === "TOUR_SCHEDULED"
          );
        });
        const hasClosedLead = matchedLeads.some((lead: any) =>
          ["LOST", "CANCELLED", "CLOSED", "WITHDRAWN"].includes(
            String(lead?.status || "").toUpperCase()
          )
        );
        const hasAction = invites.some((inv: any) =>
          isInviteActionableAccepted(inv)
        );
        const hasWaiting = invites.some((inv: any) => {
          const status = String(inv?.status || "").toUpperCase();
          return status === "SENT" || status === "NO_RESPONSE";
        });
        const allTerminalInvites = invites.every((inv: any) => {
          const status = String(inv?.status || "").toUpperCase();
          return status === "DECLINED" || status === "EXPIRED";
        });
        const workflowStatus: InviteWorkflowTab =
          isGroupClosed
            ? "closed"
            : hasCompleted
              ? "completed"
              : hasScheduled
                ? "scheduled"
                : groupHasWinner
                  ? "selected"
                  : hasAction
                    ? "action"
                    : hasWaiting
                      ? "waiting"
                      : hasClosedLead || allTerminalInvites
                        ? "closed"
                        : "all";
        return {
          groupKey,
          residentId,
          invites,
          isPrivateGroup,
          isGroupClosed,
          matchedLeadIds: matchedLeads.map((lead: any) => String(lead?.id)).filter(Boolean),
          workflowStatus,
        };
      });
  }, [invitesForMyReferrals, leadsByGroupAndBusiness, normalizeLead, isInviteActionableAccepted]);
  const payerScopedInviteEntries = React.useMemo(() => {
    switch (invitePayerTab) {
      case "private":
        return groupedInviteEntries.filter((entry: any) => entry.isPrivateGroup);
      case "nonPrivate":
        return groupedInviteEntries.filter((entry: any) => !entry.isPrivateGroup);
      default:
        return groupedInviteEntries;
    }
  }, [groupedInviteEntries, invitePayerTab]);
  const directLeadPayerCounts = React.useMemo(() => {
    const counts = {
      private: 0,
      nonPrivate: 0,
      all: 0,
    };
    (leads ?? [])
      .filter((lead: any) => !getLeadBroadcastGroupId(lead))
      .forEach((lead: any) => {
        const extra = parseExtraLoose(lead?.extra) || {};
        const normalizedPayerType = String(
          lead?.payerType || extra?.payerType || extra?.pricing?.payerType || ""
        )
          .trim()
          .toUpperCase();
        counts.all += 1;
        if (normalizedPayerType === "PRIVATE") counts.private += 1;
        else counts.nonPrivate += 1;
      });
    return counts;
  }, [leads]);
  const inviteCounts = React.useMemo(() => {
    const counts = {
      all: payerScopedInviteEntries.length,
      action: 0,
      waiting: 0,
      selected: 0,
      scheduled: 0,
      completed: 0,
      closed: 0,
    };
    payerScopedInviteEntries.forEach((entry: any) => {
      const key = entry.workflowStatus;
      if (key in counts && key !== "all") {
        counts[key as Exclude<InviteWorkflowTab, "all">] += 1;
      }
    });
    return counts;
  }, [payerScopedInviteEntries]);
  const invitePayerCounts = React.useMemo(() => {
    const counts = {
      private: directLeadPayerCounts.private,
      nonPrivate: directLeadPayerCounts.nonPrivate,
      all: groupedInviteEntries.length + directLeadPayerCounts.all,
    };
    groupedInviteEntries.forEach((entry: any) => {
      if (entry.isPrivateGroup) counts.private += 1;
      else counts.nonPrivate += 1;
    });
    return counts;
  }, [directLeadPayerCounts, groupedInviteEntries]);
  React.useEffect(() => {
    if (invitePayerTab === "private" && invitePayerCounts.private === 0) {
      setInvitePayerTab(invitePayerCounts.nonPrivate > 0 ? "nonPrivate" : "all");
      return;
    }
    if (invitePayerTab === "nonPrivate" && invitePayerCounts.nonPrivate === 0) {
      setInvitePayerTab(invitePayerCounts.private > 0 ? "private" : "all");
    }
  }, [invitePayerCounts, invitePayerTab]);
  const groupedFilteredInvites = React.useMemo(() => {
    if (inviteTab === "all") return payerScopedInviteEntries;
    return payerScopedInviteEntries.filter(
      (entry: any) => entry.workflowStatus === inviteTab
    );
  }, [payerScopedInviteEntries, inviteTab]);
  const standaloneDirectLeads = React.useMemo(() => {
    const directLeads = (leads ?? []).filter((lead: any) => !getLeadBroadcastGroupId(lead));
    if (invitePayerTab === "all") return directLeads;
    return directLeads.filter((lead: any) => {
      const extra = parseExtraLoose(lead?.extra) || {};
      const normalizedPayerType = String(
        lead?.payerType || extra?.payerType || extra?.pricing?.payerType || ""
      )
        .trim()
        .toUpperCase();
      const isPrivate = normalizedPayerType === "PRIVATE";
      return invitePayerTab === "private" ? isPrivate : !isPrivate;
    });
  }, [invitePayerTab, leads]);
  const currentPayerBreakdown = React.useMemo(() => {
    const grouped =
      invitePayerTab === "private"
        ? groupedInviteEntries.filter((entry: any) => entry.isPrivateGroup).length
        : invitePayerTab === "nonPrivate"
          ? groupedInviteEntries.filter((entry: any) => !entry.isPrivateGroup).length
          : groupedInviteEntries.length;
    const direct =
      invitePayerTab === "private"
        ? directLeadPayerCounts.private
        : invitePayerTab === "nonPrivate"
          ? directLeadPayerCounts.nonPrivate
          : directLeadPayerCounts.all;
    return { grouped, direct };
  }, [directLeadPayerCounts, groupedInviteEntries, invitePayerTab]);
  React.useEffect(() => {
    setInviteTab("all");
  }, [invitePayerTab]);
  const canMarkPlacementComplete = React.useCallback((lead: any) => {
    if (!lead?.id) return false;
    const status = String(lead?.status || "").toUpperCase();
    if (status === "PLACED" || status === "CLOSED") return false;
    const selectedBusinessId = lead?.group?.selectedBusinessId ?? lead?.selectedBusinessId ?? null;
    const businessId = lead?.business?.id ?? lead?.businessId ?? null;
    const isSelected =
      selectedBusinessId != null &&
      businessId != null &&
      String(selectedBusinessId) === String(businessId);
    return (
      isSelected ||
      status === "TOUR_SCHEDULED" ||
      status === "PENDING_DECISION"
    );
  }, []);
  const handleMarkPlacementComplete = React.useCallback(
    async (leadId: string | number) => {
      await markPlacementComplete({
        variables: { id: String(leadId), status: "PLACED" },
      });
    },
    [markPlacementComplete]
  );
  const handleCloseAsSettled = React.useCallback(
    async (leadId: string | number) => {
      await markPlacementComplete({
        variables: { id: String(leadId), status: "CLOSED" },
      });
    },
    [markPlacementComplete]
  );
  const handleCloseProviderPath = React.useCallback(
    async (leadId: string | number) => {
      await markPlacementComplete({
        variables: { id: String(leadId), status: "LOST" },
      });
    },
    [markPlacementComplete]
  );
  const handleCloseRequestGroup = React.useCallback(
    async (groupId: string | number, leadIds: Array<string | number> = []) => {
      const normalizedLeadIds = Array.from(
        new Set(leadIds.map((leadId) => String(leadId)).filter(Boolean))
      );
      if (normalizedLeadIds.length > 0) {
        await Promise.all(
          normalizedLeadIds.map((leadId) =>
            markPlacementComplete({
              variables: { id: leadId, status: "LOST" },
            })
          )
        );
      }
      await setGroupClosed({
        variables: { groupId: String(groupId), closed: true },
      });
    },
    [markPlacementComplete, setGroupClosed]
  );
  const showInviteStatusBoard =
    isLoggedIn && dataMode === "mine" && invitesForMyReferrals.length > 0;

  return (
    <Stack spacing={3}>
      {isLoggedIn && tokenMode && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            Viewing:
          </Typography>
          <Chip
            label="Token"
            color={dataMode === "token" ? "primary" : "default"}
            variant={dataMode === "token" ? "filled" : "outlined"}
            onClick={() => setDataMode("token")}
          />
          <Chip
            label="Mine"
            color={dataMode === "mine" ? "primary" : "default"}
            variant={dataMode === "mine" ? "filled" : "outlined"}
            onClick={() => setDataMode("mine")}
          />
          <Box sx={{ ml: "auto", color: "text.secondary" }}>
            <Typography variant="caption">
              Showing {leads.length} of {rawLeads.length}
            </Typography>
          </Box>
        </Stack>
      )}

      {tokenMode && <TokenEntry />}

      {dataMode === "token" && refByToken && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Referrer (token)
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {refByToken.contactName && <Chip label={refByToken.contactName} />}
            {refByToken.orgName && <Chip label={refByToken.orgName} />}
            {refByToken.email && <Chip label={refByToken.email} />}
            {refByToken.phone && <Chip label={refByToken.phone} />}
          </Stack>
        </Paper>
      )}

      {!showInviteStatusBoard &&
        (() => {
          const counts = {
            all: rawLeads?.length ?? 0,
            open: (rawLeads ?? []).filter((l: any) => isOpen(l?.status)).length,
            closed: (rawLeads ?? []).filter((l: any) => isClosed(l?.status))
              .length,
          };

          const chipStyle = (k: "all" | "open" | "closed") => ({
            color: filter === k ? ("primary" as const) : ("default" as const),
            variant: filter === k ? ("filled" as const) : ("outlined" as const),
          });

          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                Filter:
              </Typography>

              <Chip
                label={`All (${counts.all})`}
                {...chipStyle("all")}
                onClick={() => setFilter("all")}
                clickable
              />
              <Chip
                label={`Open (${counts.open})`}
                {...chipStyle("open")}
                onClick={() => setFilter("open")}
                clickable
              />
              <Chip
                label={`Closed (${counts.closed})`}
                {...chipStyle("closed")}
                onClick={() => setFilter("closed")}
                clickable
              />

              <Box sx={{ ml: "auto", color: "text.secondary" }}>
                <Typography variant="caption">
                  Showing {leads.length} of {counts.all}
                </Typography>
              </Box>
            </Stack>
          );
        })()}

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {showInviteStatusBoard
            ? "Your Requests"
            : "My Referrals"}
        </Typography>
        {showInviteStatusBoard && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <Tabs
                value={invitePayerTab}
                onChange={(
                  _: React.SyntheticEvent,
                  v: InvitePayerTab
                ) => setInvitePayerTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
              >
                <Tab value="private" label={`Private (${invitePayerCounts.private})`} />
                <Tab value="nonPrivate" label={`Non-Private (${invitePayerCounts.nonPrivate})`} />
                <Tab value="all" label={`All (${invitePayerCounts.all})`} />
              </Tabs>
              <Typography variant="caption" color="text.secondary">
                {currentPayerBreakdown.grouped} grouped • {currentPayerBreakdown.direct} direct
              </Typography>
              <Tabs
                value={inviteTab}
                onChange={(
                  _: React.SyntheticEvent,
                  v: InviteWorkflowTab
                ) =>
                  setInviteTab(v)
                }
                variant="scrollable"
                allowScrollButtonsMobile
              >
                <Tab value="all" label={`All (${inviteCounts.all})`} />
                <Tab value="action" label={`Needs Action (${inviteCounts.action})`} />
                <Tab value="waiting" label={`Waiting (${inviteCounts.waiting})`} />
                <Tab value="selected" label={`Selected (${inviteCounts.selected})`} />
                <Tab value="scheduled" label={`Scheduled (${inviteCounts.scheduled})`} />
                <Tab value="completed" label={`Completed (${inviteCounts.completed})`} />
                <Tab value="closed" label={`Closed (${inviteCounts.closed})`} />
              </Tabs>
              <Stack spacing={1}>
                {groupedFilteredInvites.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No requests in this category.
                  </Typography>
                ) : (
                  groupedFilteredInvites.map(({ groupKey, residentId, invites, isPrivateGroup, workflowStatus, isGroupClosed, matchedLeadIds }) => {
                    const winnerInviteId = String(invites?.[0]?.group?.winnerInviteId || "").trim();
                    const groupId = String(invites?.[0]?.group?.id || "").trim();
                    const requestSentAt =
                      prettyDateLabel(invites?.[0]?.group?.createdAt || invites?.[0]?.createdAt || null) ||
                      null;
                    const groupHasWinner = Boolean(winnerInviteId);
                    const selectedInvite = groupHasWinner
                      ? invites.find((inv: any) => String(inv?.id || "") === winnerInviteId) || null
                      : null;
                    const privateMatchedLead = invites
                      .map((inv: any) =>
                        normalizeLead(leadsByGroupAndBusiness.get(
                          `${String(inv?.group?.id ?? "")}::${String(inv?.business?.id ?? "")}`,
                        )),
                      )
                      .find(Boolean);
                    const privateGroupLead = normalizeLead(leadsByGroup.get(groupId));
                    const privateChatReadyLead =
                      privateMatchedLead && hasReferrerCommunicationStarted(privateMatchedLead)
                        ? privateMatchedLead
                        : null;
                    const privateBookedStateLead =
                      privateMatchedLead &&
                        (getTourScheduleFromLead(privateMatchedLead) ||
                          String(privateMatchedLead?.status || "").toUpperCase() ===
                          "TOUR_SCHEDULED")
                        ? privateMatchedLead
                        : null;
                    const privateDisplayLead =
                      privateChatReadyLead || privateBookedStateLead || null;
                    const privateBookedTour = privateChatReadyLead
                      ? getTourScheduleFromLead(privateChatReadyLead)
                      : privateBookedStateLead
                        ? getTourScheduleFromLead(privateBookedStateLead)
                        : null;
                    const privateIsTourScheduled =
                      !!privateBookedTour ||
                      String(privateDisplayLead?.status || "").toUpperCase() === "TOUR_SCHEDULED";
                    const privateOwnerAgent = invites?.[0]?.group?.ownerAgent || null;
                    const privateOwnerAgentName =
                      String(
                        privateOwnerAgent?.username ||
                          invites?.[0]?.group?.ownerAgentName ||
                          "",
                      ).trim() || null;
                    const privateOwnerAgentPhone =
                      String(privateOwnerAgent?.phone || "").trim() || null;
                    const privateGroupChatId = groupId
                      ? `broadcast-group:${groupId}`
                      : "";
                    const privateActionLeadId =
                      String(
                        privateChatReadyLead?.id ||
                          privateDisplayLead?.id ||
                          privateMatchedLead?.id ||
                          privateGroupLead?.id ||
                          matchedLeadIds[0] ||
                          privateGroupChatId ||
                          "",
                      ).trim();

                    return (
                      <Accordion
                        key={`resident-group-${inviteTab}-${groupKey}`}
                        defaultExpanded={false}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" fontWeight={700}>
                              {residentId}
                            </Typography>
                            {requestSentAt ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Sent ${requestSentAt}`}
                              />
                            ) : null}
                            {isPrivateGroup ? (
                              <Typography variant="body2" color="text.secondary">
                                {groupHasWinner
                                  ? "Provider selected"
                                  : "Concierge agent is reviewing options and finding the best care match"}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {groupHasWinner
                                  ? "Selection completed"
                                  : `${invites.length} provider${invites.length === 1 ? "" : "s"}`}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              color={
                                workflowStatus === "action"
                                  ? "warning"
                                  : workflowStatus === "waiting"
                                    ? "info"
                                    : workflowStatus === "all"
                                      ? "primary"
                                      : workflowStatus === "selected"
                                        ? "success"
                                        : workflowStatus === "scheduled"
                                          ? "success"
                                          : workflowStatus === "completed"
                                            ? "success"
                                            : "default"
                              }
                              variant={
                                workflowStatus === "completed" || workflowStatus === "selected"
                                  ? "filled"
                                  : "outlined"
                              }
                              label={
                                workflowStatus === "action"
                                  ? "Needs Action"
                                  : workflowStatus === "waiting"
                                    ? "Waiting"
                                    : workflowStatus === "all"
                                      ? "New Request"
                                      : workflowStatus === "selected"
                                        ? "Selected"
                                        : workflowStatus === "scheduled"
                                          ? "Scheduled"
                                          : workflowStatus === "completed"
                                            ? "Completed"
                                            : "Closed"
                              }
                            />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1}>
                            {!isGroupClosed && groupId ? (
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={closingGroup}
                                  onClick={() =>
                                    handleCloseRequestGroup(groupId, matchedLeadIds)
                                  }
                                >
                                  Close Entire Request
                                </Button>
                              </Box>
                            ) : null}
                            {isPrivateGroup && !privateIsTourScheduled ? (
                              <Alert severity="info">
                                {privateDisplayLead
                                  ? getConciergePortalStatusMessage(privateDisplayLead)
                                  : "Your concierge agent is working to find the best care match for this request and will update you as providers respond."}
                              </Alert>
                            ) : null}
                            {isPrivateGroup ? (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                  gap: 1.5,
                                }}
                              >
                                <ProviderContactSummary
                                  lead={privateDisplayLead || privateMatchedLead || privateGroupLead}
                                />
                                <ConciergeAgentContactCard
                                  agentName={privateOwnerAgentName}
                                  agentEmail={String(privateOwnerAgent?.email || "").trim() || null}
                                  agentPhone={privateOwnerAgentPhone}
                                />
                              </Box>
                            ) : null}
                            {isPrivateGroup && (privateOwnerAgentName || privateActionLeadId || privateChatReadyLead) ? (
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                                {privateChatReadyLead && !privateIsTourScheduled ? (
                                  <ReadyTourButtonForReferrer
                                    lead={privateChatReadyLead}
                                    refereeId={portalReferrer.id}
                                    referrerNameOverride={portalReferrer.name}
                                    referrerEmailOverride={portalReferrer.email}
                                    referrerPhoneOverride={portalReferrer.phone}
                                    bizSlug={privateChatReadyLead?.business?.url ?? null}
                                    rootClientPath={rootClientPath}
                                    label="Choose Tour Time"
                                  />
                                ) : null}
                                {privateChatReadyLead && privateIsTourScheduled ? (
                                  <ManageTourButton
                                    email={portalReferrer.email}
                                    referralLeadId={privateDisplayLead?.id ?? null}
                                    bookingId={privateBookedTour?.id ?? null}
                                    bookingTime={privateBookedTour?.bookingTime ?? null}
                                  />
                                ) : null}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    onOpenChat({
                                      id: privateActionLeadId,
                                      referrerName: portalReferrer.name,
                                    })
                                  }
                                  disabled={!privateActionLeadId}
                                >
                                  Chat with Agent
                                </Button>
                              </Box>
                            ) : null}
                            {isPrivateGroup && privateDisplayLead && privateIsTourScheduled ? (
                              <Alert severity="success">
                                {privateBookedTour
                                  ? getBookedTourMessage(
                                    privateDisplayLead,
                                    privateBookedTour,
                                  )
                                  : `Your tour has already been scheduled with ${privateDisplayLead?.business?.name || "the provider"}.`} Open My Schedule if you need to change or cancel it.
                              </Alert>
                            ) : null}
                            {isPrivateGroup && !privateChatReadyLead && (privateMatchedLead || privateOwnerAgentName) ? (
                              <Typography variant="body2" color="text.secondary">
                                Your concierge agent is working this request. You can use chat for updates, and tour booking will appear once the agent enables the next step.
                              </Typography>
                            ) : null}
                            {groupHasWinner && (
                              <Alert severity="success">
                                {selectedInvite?.business?.name
                                  ? `${selectedInvite.business.name} was selected for this request.`
                                  : "A provider was selected for this request."} This request is ready for the next step.
                              </Alert>
                            )}
                            {!isPrivateGroup ? invites.map((inv: any) => {
                                const matchedLead = normalizeLead(leadsByGroupAndBusiness.get(
                                  `${String(inv?.group?.id ?? "")}::${String(inv?.business?.id ?? "")}`
                                ));
                                const fallbackLead = buildLeadFromBroadcastInvite(inv);
                                const seed = parseBroadcastSeed(inv?.group?.seedJson);
                                const isWinner = groupHasWinner && String(inv?.id || "") === winnerInviteId;
                                const leadForCard = matchedLead
                                  ? {
                                    ...matchedLead,
                                    selectedBusinessId: isWinner
                                      ? matchedLead?.business?.id ?? matchedLead?.businessId ?? inv?.business?.id ?? null
                                      : matchedLead?.selectedBusinessId ?? null,
                                    group: {
                                      ...(matchedLead?.group || {}),
                                      id: inv?.group?.id ?? matchedLead?.group?.id ?? null,
                                      selectedBusinessId: isWinner
                                        ? matchedLead?.business?.id ?? matchedLead?.businessId ?? inv?.business?.id ?? null
                                        : matchedLead?.group?.selectedBusinessId ?? null,
                                    },
                                    extra: {
                                      ...(parseExtraLoose(matchedLead?.extra) || {}),
                                      pricing: {
                                        ...((parseExtraLoose(matchedLead?.extra) || {})?.pricing ||
                                          {}),
                                        mode:
                                          seed.payerType === "PRIVATE"
                                            ? "CONCIERGE"
                                            : ((parseExtraLoose(matchedLead?.extra) || {})?.pricing
                                              ?.mode ?? null),
                                      },
                                      referrerStatusMessage:
                                        seed.referrerStatusMessage ?? null,
                                    },
                                  }
                                  : {
                                    ...fallbackLead,
                                    selectedBusinessId: isWinner
                                      ? inv?.business?.id ?? null
                                      : fallbackLead?.selectedBusinessId ?? null,
                                    group: {
                                      ...(fallbackLead?.group || {}),
                                      id: inv?.group?.id ?? null,
                                      selectedBusinessId: isWinner
                                        ? inv?.business?.id ?? null
                                        : fallbackLead?.group?.selectedBusinessId ?? null,
                                    },
                                  };
                                const canPickProvider =
                                  !groupHasWinner &&
                                  String(inv?.status || "").toUpperCase() === "ACCEPTED";

                                return (
                                  <LeadCard
                                    key={`inv-lead-${inv.id}`}
                                    lead={leadForCard}
                                    refereeId={activeReferrerId}
                                    referrerName={portalReferrer.name}
                                    referrerEmail={portalReferrer.email}
                                    referrerPhone={portalReferrer.phone}
                                    bizSlug={matchedLead?.business?.url ?? null}
                                    onOpenChat={onOpenChat}
                                    collapsible
                                    defaultCollapsed
                                    placementCompleted={
                                      String(leadForCard?.status || "").toUpperCase() ===
                                      "PLACED"
                                    }
                                    extraActions={
                                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {canPickProvider ? (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            disabled={pickingWinner}
                                            onClick={() =>
                                              pickBroadcastWinner({
                                                variables: {
                                                  groupId: inv.group.id,
                                                  inviteId: inv.id,
                                                },
                                              })
                                            }
                                          >
                                            Pick Provider
                                          </Button>
                                        ) : null}
                                        {isWinner ? (
                                          <Chip
                                            size="small"
                                            color="success"
                                            label="Provider Selected"
                                          />
                                        ) : null}
                                        {matchedLead && canMarkPlacementComplete(leadForCard) ? (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            disabled={markingPlacementComplete}
                                            onClick={() =>
                                              handleMarkPlacementComplete(matchedLead.id)
                                            }
                                          >
                                            Mark Placement Complete
                                          </Button>
                                        ) : null}
                                        {matchedLead &&
                                          String(leadForCard?.status || "").toUpperCase() ===
                                          "PLACED" ? (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            disabled={markingPlacementComplete}
                                            onClick={() =>
                                              handleCloseAsSettled(matchedLead.id)
                                            }
                                          >
                                            Close as Settled
                                          </Button>
                                        ) : null}
                                        {matchedLead &&
                                          !isGroupClosed &&
                                          String(leadForCard?.status || "").toUpperCase() !==
                                          "PLACED" &&
                                          String(leadForCard?.status || "").toUpperCase() !==
                                          "LOST" ? (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={markingPlacementComplete}
                                            onClick={() =>
                                              handleCloseProviderPath(matchedLead.id)
                                            }
                                          >
                                            Close This Provider
                                          </Button>
                                        ) : null}
                                        {inv?.group?.id && leadForCard?.extra?.pricing?.mode !== "CONCIERGE" ? (
                                          <Button
                                            size="small"
                                            component={RouterLink}
                                            to={`/care/referrals/group/${inv.group.id}`}
                                            variant="outlined"
                                          >
                                            Open Group
                                          </Button>
                                        ) : null}
                                      </Stack>
                                    }
                                  />
                                );
                              }) : null}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Stack>
            </Stack>
          </Paper>
        )}
        {showInviteStatusBoard && standaloneDirectLeads.length > 0 ? (
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={700}>
                  Direct Requests
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${standaloneDirectLeads.length} request${standaloneDirectLeads.length === 1 ? "" : "s"}`}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {standaloneDirectLeads.map((lead: any) => {
                  const normalizedLead = normalizeLead(lead);
                  return (
                    <LeadCard
                      key={`direct-lead-${lead.id}`}
                      lead={normalizedLead}
                      refereeId={activeReferrerId}
                      referrerName={portalReferrer.name}
                      referrerEmail={portalReferrer.email}
                      referrerPhone={portalReferrer.phone}
                      bizSlug={normalizedLead?.business?.url ?? null}
                      onOpenChat={onOpenChat}
                      collapsible
                      defaultCollapsed
                      suppressConciergeBadge
                      placementCompleted={
                        String(normalizedLead?.status || "").toUpperCase() === "PLACED"
                      }
                      extraActions={
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {canMarkPlacementComplete(normalizedLead) ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={markingPlacementComplete}
                              onClick={() =>
                                handleMarkPlacementComplete(normalizedLead.id)
                              }
                            >
                              Mark Placement Complete
                            </Button>
                          ) : null}
                          {String(normalizedLead?.status || "").toUpperCase() === "PLACED" ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={markingPlacementComplete}
                              onClick={() => handleCloseAsSettled(normalizedLead.id)}
                            >
                              Close as Settled
                            </Button>
                          ) : null}
                        </Stack>
                      }
                    />
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : null}
        {!showInviteStatusBoard && (!rawLeads || rawLeads.length === 0) ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            {hasBroadcastActivity ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You have {broadcastInviteCount} broadcast invite{broadcastInviteCount === 1 ? "" : "s"}.
                A referral appears here after a provider is selected.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No referrals found.
              </Typography>
            )}
            {/* Empty-state CTA */}
            <Button
              variant="contained"
              onClick={() => navigate("/care/request?mode=broadcast")}
            >
              Create New Referral (Broadcast)
            </Button>
          </Paper>
        ) : !showInviteStatusBoard ? (
          leads.map((l: any) => {
            const normalizedLead = normalizeLead(l);
            return (
              <LeadCard
                key={`lead-${l.id}`}
                lead={normalizedLead}
                refereeId={activeReferrerId}
                referrerName={portalReferrer.name}
                referrerEmail={portalReferrer.email}
                referrerPhone={portalReferrer.phone}
                bizSlug={normalizedLead?.business?.url ?? null}
                onOpenChat={onOpenChat}
                placementCompleted={
                  String(normalizedLead?.status || "").toUpperCase() === "PLACED"
                }
                extraActions={
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {canMarkPlacementComplete(normalizedLead) ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={markingPlacementComplete}
                        onClick={() =>
                          handleMarkPlacementComplete(normalizedLead.id)
                        }
                      >
                        Mark Placement Complete
                      </Button>
                    ) : null}
                    {String(normalizedLead?.status || "").toUpperCase() === "PLACED" ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={markingPlacementComplete}
                        onClick={() => handleCloseAsSettled(normalizedLead.id)}
                      >
                        Close as Settled
                      </Button>
                    ) : null}
                  </Stack>
                }
              />
            );
          })
        ) : (
          <Button
            variant="contained"
            onClick={() => navigate("/care/request?mode=broadcast")}
            sx={{ alignSelf: "flex-start" }}
          >
            Create New Referral (Broadcast)
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export default ReferrerPortalPage;
export { };
